import axiosInstance from '../axios/axios';
import axios from 'axios';
import {
  StockReviewDecision,
  StockReviewDecisionType,
  StockMatch,
  StockReviewWithMatches,
} from '../../types/stock';

const STOCK_REVIEW_ENDPOINT = 'api/stock-review/decision';
const TABLE_API_URL = 'api/azure-table-operations';

export interface StockReviewDecisionRequest {
  ClientId: string;
  ReviewId: string;
  Decision: StockReviewDecisionType;
  SelectedStockId?: string;
  NewStockName?: string;
}

export interface StockReviewServiceError {
  message: string;
  statusCode?: number;
}

// Raw entity from Azure Table Storage
interface StockReviewTableEntity {
  PartitionKey: string;
  RowKey: string;
  ExtractedStockName: string;
  StockUnit?: string;
  VendorId?: string;
  FileName?: string;
  BatchId?: string;
  Status: string;
  MatchingStocksJson?: string;
}

// PascalCase stock match from JSON (backend format)
interface StockMatchRaw {
  StockId: string;
  StockName: string;
  StockUnit?: string;
  VendorId?: string;
  VendorName?: string;
  CompanyId?: string;
  CompanyName?: string;
  SimilarityScore: number;
}

export const stockReviewService = {
  async submitDecision(
    clientId: string,
    decision: StockReviewDecision,
  ): Promise<void> {
    try {
      const request: StockReviewDecisionRequest = {
        ClientId: clientId,
        ReviewId: decision.ReviewId,
        Decision: decision.Decision,
        SelectedStockId: decision.SelectedStockId,
        NewStockName: decision.NewStockName,
      };

      await axiosInstance.post(STOCK_REVIEW_ENDPOINT, request);
    } catch (error: unknown) {
      const serviceError = handleStockReviewError(error);
      throw serviceError;
    }
  },

  async getRequiredReviews(
    clientId: string,
    batchId: string,
  ): Promise<StockReviewWithMatches[]> {
    try {
      const request = {
        tableName: 'StockReview',
        operationName: 'getbyquery',
        query: `PartitionKey eq '${clientId.toLowerCase()}' and BatchId eq '${batchId}'`,
      };

      const response = await axiosInstance.post<StockReviewTableEntity[]>(
        TABLE_API_URL,
        request,
      );

      // Count completed items for pagination
      const completedCount = response.data.filter(
        entity => entity.Status === 'Completed',
      ).length;

      // Filter out completed items and transform raw table data to StockReviewWithMatches format
      const pendingReviews = response.data
        .filter(entity => entity.Status !== 'Completed')
        .map((entity, index) => {
          let matchingStocks: StockMatch[] = [];
          if (entity.MatchingStocksJson) {
            try {
              const rawMatches: StockMatchRaw[] = JSON.parse(
                entity.MatchingStocksJson,
              );
              // Transform PascalCase to camelCase
              matchingStocks = rawMatches.map(raw => ({
                stockId: raw.StockId,
                stockName: raw.StockName,
                stockUnit: raw.StockUnit,
                vendorId: raw.VendorId,
                vendorName: raw.VendorName,
                companyId: raw.CompanyId,
                companyName: raw.CompanyName,
                similarityScore: raw.SimilarityScore,
              }));
            } catch {
              console.warn(
                'Failed to parse MatchingStocksJson for review',
                entity.RowKey,
              );
            }
          }

          return {
            reviewId: entity.RowKey,
            extractedStockName: entity.ExtractedStockName,
            stockUnit: entity.StockUnit,
            vendorId: entity.VendorId,
            fileName: entity.FileName,
            batchId: entity.BatchId,
            matchingStocks,
            currentPage: completedCount + index + 1,
            totalItems: response.data.length,
            pageSize: 1,
          };
        });

      return pendingReviews;
    } catch (error: unknown) {
      const serviceError = handleStockReviewError(error);
      throw serviceError;
    }
  },
};
function handleStockReviewError(error: unknown): StockReviewServiceError {
  if (axios.isAxiosError(error)) {
    const statusCode = error.response?.status;

    if (statusCode === 400) {
      return {
        message: 'Invalid request. Please check your selection and try again.',
        statusCode,
      };
    }

    if (statusCode === 404) {
      return {
        message:
          'Review request not found. It may have expired or already been processed.',
        statusCode,
      };
    }

    if (statusCode === 500) {
      return {
        message: 'Server error occurred. Please try again later.',
        statusCode,
      };
    }

    if (error.code === 'ECONNABORTED') {
      return {
        message:
          'Request timed out. Please check your connection and try again.',
      };
    }

    if (!error.response) {
      return {
        message: 'Network error. Please check your internet connection.',
      };
    }

    return {
      message: error.response?.data?.message || 'An unexpected error occurred.',
      statusCode,
    };
  }

  return {
    message: 'An unexpected error occurred while submitting your decision.',
  };
}
export default stockReviewService;
